import { AlertTriangle } from 'lucide-react'
import type { LayoutTemplate } from '@/types/storage'

/**
 * What a copy brings across: the layout, and nothing else.
 *
 * A copy is the shape of a unit — its levels, grid sizes and labels. What the
 * source is held at, its blocked slots, its contents, its picking zones, its
 * name and the room it stands in all belong to that one machine, so they stay
 * with it; the new unit is set up for itself. There is deliberately no choice
 * here: an option to carry the temperature across was ticked by default, and a
 * copied freezer quietly inherited another machine's settings.
 *
 * Shared by both entry points — "copy FROM" in the setup wizard and "copy TO"
 * from the units table — so the two cannot describe the act differently.
 */
export function CopyLayoutOptions({ template }: { template: LayoutTemplate }) {
  return (
    <div className='space-y-2.5'>
      {/* A level applies to every parent above it, so a source whose racks
          disagree cannot be reproduced exactly. Said plainly rather than
          copied silently — the levels offered are the dominant shape. */}
      {!template.uniform && (
        <div className='flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[13px] text-amber-900'>
          <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          <div>
            <p className='font-medium'>{template.sourceCode} is not built to one uniform shape.</p>
            {template.notes.map((note) => (
              <p key={note}>{note}</p>
            ))}
            <p>Check the levels before building.</p>
          </div>
        </div>
      )}

      <p className='text-[13px]'>
        Only the layout is copied: its levels, sizes and labels.
      </p>
      <p className='text-[12px] text-muted-foreground'>
        What {template.sourceCode} is held at, its blocked slots, contents, picking zones, name and room stay with it.
        Set those on the new unit itself.
      </p>
    </div>
  )
}
